/**
 * ModifierRolePage
 * Formulaire de modification d'un rôle + assignation des permissions
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Loader2, CheckCircle2, Lock } from 'lucide-react';
import {
  useRole,
  useModifierRole,
  useAssignerPermissions,
  usePermissions,
} from '../../infrastructure/api/useRoles';
import type { PermissionDTO } from '../../application/dto/dtos';
import { grouperPermissionsParRessource } from '../store/rolesStore';

export function ModifierRolePage() {
  const navigate = useNavigate();
  const { idRole } = useParams<{ idRole: string }>();

  const { data: role, isLoading: roleLoading, isError } = useRole(idRole ?? '');
  const { data: permissions, isLoading: permissionsLoading } = usePermissions();
  const modifierRole = useModifierRole();
  const assignerPermissions = useAssignerPermissions();

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    isActive: true,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Pré-remplir le formulaire depuis le rôle chargé
  useEffect(() => {
    if (role) {
      setFormData({
        nom: role.nom,
        description: role.description ?? '',
        isActive: role.isActive,
      });
      setSelectedPermissions(new Set(role.permissions.map((p: PermissionDTO) => p.idPermission)));
    }
  }, [role]);

  const togglePermission = (idPermission: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(idPermission)) {
        next.delete(idPermission);
      } else {
        next.add(idPermission);
      }
      return next;
    });
  };

  const toggleRessource = (permsRessource: PermissionDTO[]) => {
    const tous = permsRessource.every((p) => selectedPermissions.has(p.idPermission));
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      for (const p of permsRessource) {
        if (tous) {
          next.delete(p.idPermission);
        } else {
          next.add(p.idPermission);
        }
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!idRole) return;
    setError(null);

    try {
      // 1. Mettre à jour les infos du rôle
      await modifierRole.mutateAsync({
        idRole,
        data: {
          nom: formData.nom,
          description: formData.description || undefined,
          isActive: formData.isActive,
        },
      });

      // 2. Assigner les permissions
      await assignerPermissions.mutateAsync({
        idRole,
        data: { idsPermissions: Array.from(selectedPermissions) },
      });

      setSuccess(true);
      setTimeout(() => navigate('/admin/roles'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    }
  };

  const permissionsGroupees = grouperPermissionsParRessource(permissions ?? []);
  const isLoading = roleLoading || permissionsLoading;
  const isSaving = modifierRole.isLoading || assignerPermissions.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Chargement...</span>
      </div>
    );
  }

  if (isError || !role) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-600 dark:text-red-400">Rôle introuvable</p>
        <button
          onClick={() => navigate('/admin/roles')}
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/roles')}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                   rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Modifier le rôle
            {role.isSystemRole && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100
                             text-yellow-700 rounded text-sm font-normal ml-2">
                <Lock className="w-3.5 h-3.5" />
                Système
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {role.nom} — <span className="font-mono">{role.slug}</span>
          </p>
        </div>
      </div>

      {/* Avertissement rôle système */}
      {role.isSystemRole && (
        <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border
                       border-yellow-200 dark:border-yellow-800 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800 dark:text-yellow-200">Rôle système</p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              Le slug et le niveau de ce rôle système sont immuables.
              Seuls le nom, la description et les permissions peuvent être modifiés.
            </p>
          </div>
        </div>
      )}

      {/* Message de succès */}
      {success && (
        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border
                       border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          Rôle modifié avec succès ! Redirection...
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border
                       border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations générales */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200
                       dark:border-gray-700 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Informations générales
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nom *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData((prev) => ({ ...prev, nom: e.target.value }))}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slug
              </label>
              <input
                type="text"
                value={role.slug}
                disabled
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md
                         bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-500 font-mono
                         cursor-not-allowed"
              />
              <p className="text-xs text-gray-400 mt-1">Non modifiable</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Niveau hiérarchique
            </label>
            <input
              type="number"
              value={role.niveau}
              disabled
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md
                       bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-500
                       cursor-not-allowed"
            />
            <p className="text-xs text-gray-400 mt-1">
              {role.isSystemRole ? 'Non modifiable (rôle système)' : 'Non modifiable après création'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
              Rôle actif (visible et assignable aux utilisateurs)
            </label>
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200
                       dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">
              Permissions ({selectedPermissions.size} sélectionnée{selectedPermissions.size > 1 ? 's' : ''})
            </h2>
          </div>

          {permissionsGroupees.length === 0 ? (
            <p className="text-sm text-gray-500 italic">Aucune permission disponible</p>
          ) : (
            <div className="space-y-6">
              {permissionsGroupees.map(({ ressource, permissions: perms }) => {
                const toutesSelectionnees = perms.every((p) =>
                  selectedPermissions.has(p.idPermission),
                );
                const certainesSelectionnees = perms.some((p) =>
                  selectedPermissions.has(p.idPermission),
                );

                return (
                  <div key={ressource}>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={toutesSelectionnees}
                        ref={(el) => {
                          if (el) el.indeterminate = !toutesSelectionnees && certainesSelectionnees;
                        }}
                        onChange={() => toggleRessource(perms)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="font-medium text-gray-800 dark:text-gray-200 capitalize">
                        {ressource}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({perms.length} action{perms.length > 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="ml-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {perms.map((perm) => (
                        <label
                          key={perm.idPermission}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPermissions.has(perm.idPermission)}
                            onChange={() => togglePermission(perm.idPermission)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {perm.action}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={() => navigate('/admin/roles')}
            disabled={isSaving}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600
                     text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50
                     dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSaving || success}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                     transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enregistrement...
              </>
            ) : (
              'Enregistrer les modifications'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
